import React, { type ComponentProps, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { useHovered } from '@garnish/core';
import { webOnlyStyles } from '@garnish/core';

import { CONTAINER_PADDING_LARGE, CONTAINER_PADDING_X_MEDIUM } from '../../ProductCardV2.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductCardV2Container = (props: Props) => {
  const { children, canHover, size = 'md', style, ...viewProps } = props;

  const ref = useRef(null);
  const isHovered = useHovered(ref);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    size === 'md' ? styles.containerMD : styles.containerLG,
    canHover && isHovered ? styles.containerHover : undefined,
    canHover ? containerWebStyles : undefined,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View ref={ref} style={containerStyles} {...viewProps}>
      {children}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    rowGap: theme.spacing['1'],
  },
  containerMD: {
    paddingTop: theme.spacing['2'],
    paddingHorizontal: CONTAINER_PADDING_X_MEDIUM,
    paddingBottom: theme.spacing['6'],
  },
  containerLG: {
    padding: CONTAINER_PADDING_LARGE,
  },
  containerHover: {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
});

const containerWebStyles = webOnlyStyles({
  cursor: 'pointer',
  transition: `background-color ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = ComponentProps<typeof View> & {
  canHover: boolean;
  size?: 'md' | 'lg';
};
